import React, { memo, useCallback } from "react";
import { TextField, Select, MenuItem, InputAdornment, IconButton } from "@mui/material";
import { QUANTITY_SPINNER_DOWN, QUANTITY_SPINNER_UP } from "../QuantitySpinnerIcons/QuantitySpinnerIcons";
import modalStyles from "../MultiAccountOrderModal/MultiAccountOrderModal.module.css";
import { numberInputDisplayValue } from "../../utils/numberInputDisplay";
import { exchangeForcesZeroDisclosed } from "../../utils/squareOffDisclosedQuantity";

const BID_ASK_NUMBER_FIELD_SX = {
  "& .MuiInputBase-input": {
    textAlign: "center",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
  "& .MuiOutlinedInput-input": {
    textAlign: "center",
  },
  "& input[type='number']": {
    textAlign: "center",
  },
};

function SpinnerControlButtons({ onIncrement, onDecrement }) {
  return (
    <div className={modalStyles["spinner-controls"]}>
      <IconButton
        type="button"
        size="small"
        onClick={onIncrement}
        className={modalStyles["spinner-button"]}
        aria-label="Increase value"
      >
        {QUANTITY_SPINNER_UP}
      </IconButton>
      <IconButton
        type="button"
        size="small"
        onClick={onDecrement}
        className={modalStyles["spinner-button"]}
        aria-label="Decrease value"
      >
        {QUANTITY_SPINNER_DOWN}
      </IconButton>
    </div>
  );
}

function OrderRowSpinnerAdornment({ onIncrement, onDecrement }) {
  return (
    <InputAdornment position="end">
      <SpinnerControlButtons onIncrement={onIncrement} onDecrement={onDecrement} />
    </InputAdornment>
  );
}

/**
 * One account row in SQ OFF / Add More modal. Memoized so typing in one row
 * does not re-render other rows when there are many accounts.
 */
function SqOffModalAccountRow({
  modalId,
  brokerLabel,
  quantity,
  disclosedQuantity,
  rowExchange,
  orderType,
  bidAskPct,
  bidAskSelectMenuProps,
  onFieldChange,
  bumpQuantity,
  bumpDisclosed,
}) {
  const disclosedQtyLocked = exchangeForcesZeroDisclosed(rowExchange);
  const onQtyChange = useCallback(
    (e) => onFieldChange(modalId, "quantity", e.target.value),
    [modalId, onFieldChange]
  );
  const onDisclosedChange = useCallback(
    (e) => onFieldChange(modalId, "disclosedQuantity", e.target.value),
    [modalId, onFieldChange]
  );
  const onOrderTypeChange = useCallback(
    (e) => onFieldChange(modalId, "orderType", e.target.value),
    [modalId, onFieldChange]
  );
  const onBidAskChange = useCallback(
    (e) => onFieldChange(modalId, "bidAskPct", e.target.value),
    [modalId, onFieldChange]
  );

  const onQtyInc = useCallback(() => bumpQuantity(modalId, 1), [modalId, bumpQuantity]);
  const onQtyDec = useCallback(() => bumpQuantity(modalId, -1), [modalId, bumpQuantity]);
  const onDiscInc = useCallback(() => bumpDisclosed(modalId, 1), [modalId, bumpDisclosed]);
  const onDiscDec = useCallback(() => bumpDisclosed(modalId, -1), [modalId, bumpDisclosed]);

  const quantityInputProps = {
    endAdornment: <OrderRowSpinnerAdornment onIncrement={onQtyInc} onDecrement={onQtyDec} />,
  };
  const disclosedInputProps = disclosedQtyLocked
    ? {}
    : {
        endAdornment: <OrderRowSpinnerAdornment onIncrement={onDiscInc} onDecrement={onDiscDec} />,
      };

  return (
    <div className={modalStyles["modal-row"]}>
      <div className={modalStyles["modal-account-cell"]}>
        <span className={modalStyles["modal-account-label"]}>{brokerLabel || "-"}</span>
      </div>
      <div className={modalStyles["modal-quantity-cell"]}>
        <TextField
          size="small"
          type="number"
          value={numberInputDisplayValue(quantity)}
          onChange={onQtyChange}
          className={modalStyles["modal-input"]}
          inputProps={{ min: 0, step: 1 }}
          InputProps={quantityInputProps}
        />
      </div>
      <div className={modalStyles["modal-disclosed-cell"]}>
        <TextField
          size="small"
          type="number"
          value={numberInputDisplayValue(disclosedQtyLocked ? 0 : disclosedQuantity)}
          onChange={disclosedQtyLocked ? undefined : onDisclosedChange}
          className={modalStyles["modal-input"]}
          inputProps={{ min: 0, step: 1 }}
          InputProps={disclosedInputProps}
          disabled={disclosedQtyLocked}
          title={
            disclosedQtyLocked
              ? "Disclosed quantity is not used for this exchange (fixed at 0)"
              : undefined
          }
        />
      </div>
      <div className={modalStyles["modal-bidask-cell"]}>
        <div className={modalStyles["modal-bidask-container"]}>
          <Select
            value={orderType}
            size="small"
            className={modalStyles["modal-bidask-select"]}
            onChange={onOrderTypeChange}
            MenuProps={bidAskSelectMenuProps}
          >
            <MenuItem value="Bid-Ask%">Bid-Ask%</MenuItem>
            <MenuItem value="LIMIT">Limit order</MenuItem>
          </Select>
            <TextField
              size="small"
              type="number"
              value={numberInputDisplayValue(bidAskPct)}
            onChange={onBidAskChange}
            className={modalStyles["modal-bidask-number"]}
            sx={BID_ASK_NUMBER_FIELD_SX}
          />
        </div>
      </div>
    </div>
  );
}

export default memo(SqOffModalAccountRow);
